import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Image, Text, Group, Menu, ActionIcon, SimpleGrid, Center, Stack, Loader, Modal } from "@mantine/core";
import { IconPlus, IconDots, IconWorld, IconEyeOff, IconTrash, IconPencil, IconHeart, IconGitFork, IconAlertTriangle } from "@tabler/icons-react";
import { LikersModal } from "./likers-modal";
import { useUserStore } from "../stores/userStore";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function DashboardPage() {
  const [variants, setVariants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const token = useUserStore((state) => state.token);

  const [likers, setLikers] = useState([]);
  const [isLikersModalOpen, setIsLikersModalOpen] = useState(false);
  const [selectedVariantName, setSelectedVariantName] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchVariants = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/variants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch variants');
      const data = await response.json();
      setVariants(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  const handleDeleteVariant = async (variantId) => {
    const variant = variants.find(v => v.id === variantId);
    setVariantToDelete(variant);
    setDeleteModalOpen(true);
  };

  const confirmDeleteVariant = async () => {
    if (!variantToDelete) return;

    const originalVariants = [...variants];
    setVariants(variants.filter(v => v.id !== variantToDelete.id));
    setIsDeleting(true);

    try {
      const response = await fetch(`${API_URL}/api/variants/${variantToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        setVariants(originalVariants);
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete variant');
      }
      setDeleteModalOpen(false);
      setVariantToDelete(null);
    } catch (err) {
      setError(err.message);
      setVariants(originalVariants);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTogglePublic = async (variantId) => {
    const originalVariants = [...variants];
    const variantToUpdate = variants.find(v => v.id === variantId);
    if (!variantToUpdate) return;

    setVariants(variants.map(v => v.id === variantId ? { ...v, isPublic: !v.isPublic } : v));

    try {
      const response = await fetch(`${API_URL}/api/variants/${variantId}/toggle-public`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        setVariants(originalVariants);
        const data = await response.json();
        throw new Error(data.message || 'Failed to toggle public status');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLoadVariant = (variant) => {
    navigate(`/generator?variantId=${variant.id}`);
  };

  const handleShowLikers = async (variant) => {
    if (variant.likes.length === 0) return;
    try {
      const response = await fetch(`${API_URL}/api/variants/${variant.id}/likers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch likers');
      }
      const data = await response.json();
      setLikers(data);
      setSelectedVariantName(variant.name);
      setIsLikersModalOpen(true);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Stack h="100%">
      <Group justify="space-between">
        <Text size="xl" fw={600}>My Variants</Text>
        <Button component={Link} to="/generator" leftSection={<IconPlus size={16} />}>
          Generate New Variant
        </Button>
      </Group>
      
      {error && <Text c="red" size="sm">{error}</Text>}
      
      {isLoading ? (
        <Center h={200}>
            <Loader color="cyan" />
        </Center>
      ) : variants.length === 0 ? (
        <Center h={300} style={{ border: '1px dashed var(--mantine-color-dimmed)', borderRadius: 'var(--mantine-radius-md)' }}>
          <Stack align="center" gap="xs">
            <Text size="xl" fw={700}>You have no variants</Text>
            <Text c="dimmed" size="sm">Get started by generating a new one.</Text>
            <Button component={Link} to="/generator" mt="md">Generate Variant</Button>
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
          {variants.map((variant) => (
            <Card key={variant.id} padding="lg" radius="md" withBorder>
              <Card.Section>
                {variant.image ? (
                  <Image
                    src={variant.image}
                    height={220}
                    alt={variant.name}
                    fit="cover"
                  />
                ) : (
                  <div style={{ height: 220, backgroundColor: 'var(--mantine-color-gray-1)' }} />
                )}
              </Card.Section>

              <Stack mt="md" mb="xs" gap={5}>
                <Group justify="space-between" wrap="nowrap">
                    <Text fw={500} truncate>{variant.name}</Text>
                    {variant.isPublic && <IconWorld size={16} style={{ color: 'var(--mantine-color-blue-5)' }} />}
                </Group>
                
                <Text size="xs" c="dimmed">
                    Created on {new Date(variant.createdAt).toLocaleDateString()}
                </Text>
                
                {variant.forkedFrom && (
                    <Link to={`/generator?variantId=${variant.forkedFrom.id}`} style={{ textDecoration: 'none', cursor: 'pointer' }}>
                        <Group gap={4} style={{ cursor: 'pointer' }}>
                            <IconGitFork size={12} style={{ color: 'var(--mantine-color-dimmed)' }} />
                            <Text size="xs" c="dimmed" style={{ cursor: 'pointer' }} className="hover-underline">
                                Forked from {variant.forkedFrom.user.name}/{variant.forkedFrom.name}
                            </Text>
                        </Group>
                    </Link>
                )}
              </Stack>

              <Group justify="space-between" mt="md">
                <Group gap={5}>
                    <IconHeart size={16} style={{ color: 'var(--mantine-color-dimmed)' }} />
                    <Text 
                        size="sm" 
                        c="dimmed" 
                        style={{ cursor: variant.likes?.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => handleShowLikers(variant)}
                    >
                        {variant.likes?.length || 0} likes
                    </Text>
                </Group>

                <Group gap="xs">
                    <Button variant="light" size="xs" leftSection={<IconPencil size={14} />} onClick={() => handleLoadVariant(variant)}>
                        Edit
                    </Button>
                    <Menu position="bottom-end" shadow="md">
                        <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                                <IconDots size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item 
                                leftSection={variant.isPublic ? <IconEyeOff size={14} /> : <IconWorld size={14} />} 
                                onClick={() => handleTogglePublic(variant.id)}
                            >
                                {variant.isPublic ? "Make Private" : "Make Public"}
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item 
                                color="red" 
                                leftSection={<IconTrash size={14} />} 
                                onClick={() => handleDeleteVariant(variant.id)}
                            >
                                Delete
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Group>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
      <LikersModal isOpen={isLikersModalOpen} setIsOpen={setIsLikersModalOpen} likers={likers} variantName={selectedVariantName} />

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setVariantToDelete(null);
        }}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={24} color="var(--mantine-color-red-6)" />
            <Text fw={600}>Delete Variant</Text>
          </Group>
        }
        centered
      >
        <Stack>
          <Text>
            Are you sure you want to delete <Text component="span" fw={600}>{variantToDelete?.name}</Text>?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone. This variant will be permanently deleted.
          </Text>
          {variantToDelete?.isPublic && (
            <Text size="sm" c="orange" fw={500}>
              ⚠️ This is a public variant. Deleting it will remove it from the community page.
            </Text>
          )}
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteModalOpen(false);
                setVariantToDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmDeleteVariant}
              loading={isDeleting}
            >
              Delete Variant
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
